import { useMemo, useState } from "react";
import React from "react";

/**
 * Shared client-side sorting for tables. Returns a stable {sortedRows, header}
 * pair. Pass an ordered array of column definitions:
 *   [{ key, label, numeric?: bool, align?: "left"|"right", getValue?: fn }]
 *
 * Usage:
 *   const { sortedRows, header } = useTableSort(items, [...cols], "date", "desc");
 *   <table><thead>{header}</thead><tbody>{sortedRows.map(...)}</tbody></table>
 */
export function useTableSort(rows, columns, defaultKey, defaultDir = "asc", options = {}) {
  const { trailingActionColumn = true, storageKey } = options;
  const [sortBy, setSortBy] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(`sort:${storageKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.key && parsed.dir) return parsed;
        }
      } catch { /* ignore */ }
    }
    return { key: defaultKey, dir: defaultDir };
  });

  // Persist chosen sort per-page so users see the same order after reload
  React.useEffect(() => {
    if (!storageKey) return;
    try { window.localStorage.setItem(`sort:${storageKey}`, JSON.stringify(sortBy)); } catch { /* ignore */ }
  }, [sortBy, storageKey]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const col = columns.find((c) => c.key === sortBy.key);
    if (!col) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      let va = col.getValue ? col.getValue(a) : a?.[col.key];
      let vb = col.getValue ? col.getValue(b) : b?.[col.key];
      if (col.numeric) {
        va = Number(va || 0); vb = Number(vb || 0);
      } else {
        va = (va ?? "").toString().toLowerCase();
        vb = (vb ?? "").toString().toLowerCase();
      }
      if (va < vb) return sortBy.dir === "asc" ? -1 : 1;
      if (va > vb) return sortBy.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortBy, columns]);

  const toggle = (key) => setSortBy((prev) => {
    if (prev.key !== key) {
      const col = columns.find((c) => c.key === key);
      return { key, dir: col?.numeric || key.includes("date") ? "desc" : "asc" };
    }
    return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
  });

  const header = (
    <tr>
      {columns.map((c) => (
        <th key={c.key} className={c.align === "right" ? "text-right" : ""}>
          <button
            type="button"
            onClick={() => toggle(c.key)}
            className="inline-flex items-center gap-1 hover:text-slate-900"
            data-testid={`sort-${c.key}`}
          >
            {c.label}
            <span className="text-[10px] text-slate-400">
              {sortBy.key === c.key ? (sortBy.dir === "asc" ? "▲" : "▼") : "↕"}
            </span>
          </button>
        </th>
      ))}
      {trailingActionColumn && <th></th>}
    </tr>
  );

  return { sortedRows, header, sortBy, toggle };
}
